import React from 'react';
import { Helmet } from 'react-helmet';
import { matchPath, useLocation } from 'react-router-dom';
import {
    SITE_NAME,
    SITE_URL,
    DEFAULT_SOCIAL_IMAGE,
    DEFAULT_SOCIAL_IMAGE_ALT,
    TWITTER_HANDLE,
    INDEXABLE_STATIC_ROUTES,
    NOINDEX_ROUTE_CONFIGS,
    getBlogCategorySeo,
    getBlogPostSeo
} from './routeSeoData.browser';

function getMarkdownPathForRoute(routePath) {
    if (!routePath) return null;

    const withoutQuery = String(routePath).split('?')[0].split('#')[0] || '/';
    const normalizedPath =
        withoutQuery === '/' || !withoutQuery.endsWith('/') ? withoutQuery : withoutQuery.slice(0, -1);

    if (normalizedPath === '/guides' || normalizedPath.startsWith('/guides/')) {
        return `${normalizedPath}.md`;
    }

    return normalizedPath === '/' ? '/index.md' : `${normalizedPath}.md`;
}

function buildRouteSeo(rawPathname, hasToken) {
    // Route SEO entries are defined without trailing slashes; normalize incoming
    // paths so direct requests and client-side navigation match the same entry.
    const pathname =
        rawPathname.length > 1 ? rawPathname.replace(/\/+$/, '') || '/' : rawPathname;

    if (pathname === '/') {
        const homeRoute = INDEXABLE_STATIC_ROUTES.find((route) => route.path === '/');

        if (!homeRoute) {
            return null;
        }

        if (hasToken) {
            return {
                title: 'Luckynote',
                canonical: homeRoute.canonical,
                preloadImage: homeRoute.preloadImage
            };
        }

        return homeRoute;
    }

    const staticRoute = INDEXABLE_STATIC_ROUTES.find((route) => route.path === pathname);

    if (staticRoute) {
        return staticRoute;
    }

    const blogCategoryMatch = matchPath(pathname, {
        path: '/blog/category/:categorySlug',
        exact: true
    });

    if (blogCategoryMatch) {
        return (
            getBlogCategorySeo(blogCategoryMatch.params.categorySlug) || {
                title: 'Blog Category Not Found - Luckynote',
                description: "The blog category you're looking for doesn't exist.",
                robots: 'noindex,nofollow'
            }
        );
    }

    const blogMatch = matchPath(pathname, {
        path: '/blog/:slug',
        exact: true
    });

    if (blogMatch) {
        return (
            getBlogPostSeo(blogMatch.params.slug) || {
                title: 'Post Not Found - Luckynote',
                description: "The blog post you're looking for doesn't exist.",
                robots: 'noindex,nofollow'
            }
        );
    }

    return NOINDEX_ROUTE_CONFIGS.find((route) =>
        matchPath(pathname, {
            path: route.path,
            exact: route.exact !== false
        })
    ) || null;
}

function renderMetaTag(meta, index, keyPrefix) {
    const key = `${keyPrefix}-${meta.property || meta.name}-${index}`;

    if (meta.property) {
        return <meta key={key} property={meta.property} content={meta.content} />;
    }

    if (meta.name) {
        return <meta key={key} name={meta.name} content={meta.content} />;
    }

    return null;
}

function RouteSeo() {
    const location = useLocation();
    const hasToken =
        typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
            ? Boolean(window.localStorage.getItem('token'))
            : false;
    const seo = buildRouteSeo(location.pathname, hasToken);

    if (!seo) {
        return null;
    }

    const socialImage = seo.socialImage || DEFAULT_SOCIAL_IMAGE;
    const ogType = seo.ogType || 'website';
    const twitterCard = seo.twitterCard || 'summary_large_image';
    const isNoindex = Boolean(seo.robots);
    const markdownPath = !isNoindex ? getMarkdownPathForRoute(location.pathname) : null;
    const markdownUrl = markdownPath ? `${SITE_URL}${markdownPath}` : null;
    const preloadImageUrl = seo.preloadImage
        ? `${process.env.PUBLIC_URL || ''}${seo.preloadImage}`
        : null;

    return (
        <Helmet defer={false}>
            {seo.title ? <title>{seo.title}</title> : null}
            {seo.canonical ? <link rel="canonical" href={seo.canonical} /> : null}
            {markdownUrl ? <link rel="alternate" type="text/markdown" href={markdownUrl} /> : null}
            {preloadImageUrl ? <link rel="preload" as="image" href={preloadImageUrl} type="image/webp" fetchpriority="high" /> : null}
            {seo.description ? <meta name="description" content={seo.description} /> : null}
            {seo.robots ? <meta name="robots" content={seo.robots} /> : null}
            {seo.keywords ? <meta name="keywords" content={seo.keywords} /> : null}

            {!isNoindex && seo.title ? <meta property="og:title" content={seo.title} /> : null}
            {!isNoindex && seo.description ? <meta property="og:description" content={seo.description} /> : null}
            {!isNoindex ? <meta property="og:type" content={ogType} /> : null}
            {!isNoindex && seo.canonical ? <meta property="og:url" content={seo.canonical} /> : null}
            {!isNoindex ? <meta property="og:site_name" content={SITE_NAME} /> : null}
            {!isNoindex ? <meta property="og:locale" content="en_US" /> : null}
            {!isNoindex ? <meta property="og:image" content={socialImage} /> : null}
            {!isNoindex ? <meta property="og:image:secure_url" content={socialImage} /> : null}
            {!isNoindex ? <meta property="og:image:width" content="1672" /> : null}
            {!isNoindex ? <meta property="og:image:height" content="941" /> : null}
            {!isNoindex ? <meta property="og:image:alt" content={seo.title || DEFAULT_SOCIAL_IMAGE_ALT} /> : null}

            {!isNoindex ? <meta name="twitter:card" content={twitterCard} /> : null}
            {!isNoindex ? <meta name="twitter:site" content={TWITTER_HANDLE} /> : null}
            {!isNoindex && seo.title ? <meta name="twitter:title" content={seo.title} /> : null}
            {!isNoindex && seo.description ? <meta name="twitter:description" content={seo.description} /> : null}
            {!isNoindex ? <meta name="twitter:image" content={socialImage} /> : null}
            {!isNoindex ? <meta name="twitter:image:src" content={socialImage} /> : null}
            {!isNoindex ? <meta name="twitter:image:alt" content={seo.title || DEFAULT_SOCIAL_IMAGE_ALT} /> : null}

            {(seo.extraMeta || []).map((meta, index) =>
                renderMetaTag(meta, index, location.pathname)
            )}

            {(seo.structuredData || []).map((schema, index) => (
                <script
                    key={`${location.pathname}-schema-${index}`}
                    type="application/ld+json"
                >
                    {JSON.stringify(schema)}
                </script>
            ))}
        </Helmet>
    );
}

export default RouteSeo;
